"use client"

import React, { PropsWithChildren, useState } from "react"
import { createQuoteRequestAction } from "@/actions/basketActions"
import { useUbStorefrontState } from "@/store/ubStorefrontState"
import { LinkRO } from "@ub/quoting-client"
import { FileDown, NotebookPenIcon } from "lucide-react"

import { useHasHover } from "@/lib/helper"
import { CustomerData } from "@/lib/intershop/interfaces/customer/customer"
import { UserData } from "@/lib/intershop/interfaces/user/user"
import { Button, Popover, PopoverContent, PopoverTrigger } from "@/components/ui/components"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/components/tooltip"
import { isSbLocaleDach } from "@/components/ui/helpers/utils"
import { UbBasketActionsStoryblok } from "@/components/storyblok/component-types"

interface BasketActionsProps extends PropsWithChildren {
  blok: UbBasketActionsStoryblok
  isB2B: boolean
  customerData?: CustomerData
  userData?: UserData | undefined
  sbLocale: string
  tooltip_content: React.ReactNode
}

export default function BasketActionsClient(props: BasketActionsProps) {
  const { basketLineItems, setIsLoading, isOCI } = useUbStorefrontState()
  const hasHover = useHasHover()

  const [tooltipVisible, setTooltipVisible] = useState(false)

  function createQuoteRequest() {
    setIsLoading(true)
    createQuoteRequestAction(props.customerData, props.userData).then((result: LinkRO) => {
      setIsLoading(false)
      window.location.href = props.blok.request_quote_path + "" + result.title
    })
  }

  return (
    <div className={"flex flex-row flex-wrap justify-start gap-2"}>
      {basketLineItems && (
        <>
          {props.isB2B && props.blok && (
            <a className={""} href={props.blok["save_pdf_path"]}>
              <Button className={""} variant={"outline_secondary"} size={"defaultNoGrow"}>
                <div className={"flex w-full flex-row items-center justify-between gap-1"}>
                  <p>{props.blok["label_save_as_pdf"]}</p>
                  <FileDown />
                </div>
              </Button>
            </a>
          )}

          {!isOCI && props.isB2B && isSbLocaleDach(props.sbLocale) && (
            <>
              {props.userData ? (
                <>
                  <Button className={""} variant={"outline_secondary"} size={"defaultNoGrow"}>
                    <div className={"flex flex-row items-center justify-between gap-1"} onClick={createQuoteRequest}>
                      <p>{props.blok["label_request_quote"]}</p>
                      <NotebookPenIcon />
                    </div>
                  </Button>
                </>
              ) : (
                <>
                  {hasHover ? (
                    <TooltipProvider>
                      <Tooltip open={tooltipVisible}>
                        <TooltipTrigger
                          asChild
                          onMouseEnter={() => setTooltipVisible(true)}
                          // If the element is focused, which would have required click or keyboard navigation
                          // don't hide the tooltip on mouse leave
                          onMouseLeave={(e) => /*e.target !== document.activeElement &&*/ setTooltipVisible(false)}
                          onFocus={() => setTooltipVisible(true)}
                          onBlur={() => setTooltipVisible(false)}
                        >
                          <Button
                            aria-disabled="true"
                            className={""}
                            variant={"outline_secondary"}
                            size={"defaultNoGrow"}
                          >
                            <div className={"flex flex-row items-center justify-between gap-1"}>
                              <p>{props.blok["label_request_quote"]}</p>
                              <NotebookPenIcon />
                            </div>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className={"p-0"}>
                          <div>{props.tooltip_content}</div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          aria-disabled="true"
                          className={""}
                          variant={"outline_secondary"}
                          size={"defaultNoGrow"}
                        >
                          <div className={"flex flex-row items-center justify-between gap-1"}>
                            <p>{props.blok["label_request_quote"]}</p>
                            <NotebookPenIcon />
                          </div>
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-fit">
                        <div className={"w-96"}>{props.tooltip_content}</div>
                      </PopoverContent>
                    </Popover>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
